/* src/components/product/ProductSearchAndBrowse.tsx */

"use client";

import { useState, useMemo } from "react";
import { ProductBrowser } from "~/components/product/ProductBrowser";
import { Search } from "lucide-react";

// 从页面组件中借用的类型定义
type ProductPreview = { id: string; name: string; imageUrl: string };
type Catalogue = {
	id: string;
	name: string;
	products: ProductPreview[];
};

// 定义组件的 props 类型
type ProductSearchAndBrowseProps = {
	initialCatalogues: Catalogue[];
	texts: {
		categoriesTitle: string;
		noProducts: string;
		searchPlaceholder: string;
	};
};

export function ProductSearchAndBrowse({
	initialCatalogues,
	texts,
}: ProductSearchAndBrowseProps) {
	const [searchQuery, setSearchQuery] = useState("");

	const filteredCatalogues = useMemo(() => {
		if (!searchQuery.trim()) {
			return initialCatalogues;
		}

		// --- 已实现: 忽略大小写匹配 ---
		// 1. 将搜索词转换为小写
		const lowercasedQuery = searchQuery.toLowerCase();

		return initialCatalogues.map((catalogue) => ({
			...catalogue,
			products: catalogue.products.filter((product) =>
				// 2. 将产品名称也转换为小写再进行比较
				product.name.toLowerCase().includes(lowercasedQuery)
			),
		}));
	}, [searchQuery, initialCatalogues]);

	return (
		<div>
			<div className="mb-12">
				<label htmlFor="product-search" className="sr-only">
					Search Products
				</label>
				<div className="relative rounded-md shadow-sm">
					<div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
						<Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
					</div>
					<input
						type="text"
						name="product-search"
						id="product-search"
						// --- 修正: 优化聚焦状态的样式 ---
						// 移除了 focus:ring-* 类，仅保留边框颜色的变化，消除了“外层”感
						className="block w-full rounded-full border-2 border-gray-200 bg-gray-50 py-3 pl-11 pr-4 text-gray-900 placeholder:text-gray-400 focus:border-[#1a3d5d] focus:outline-none sm:text-sm sm:leading-6 transition-colors duration-200"
						placeholder={texts.searchPlaceholder}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>
			</div>

			<div>
				<h2 className="text-2xl font-bold text-gray-800 mb-6">
					{texts.categoriesTitle}
				</h2>
				<ProductBrowser
					catalogues={filteredCatalogues}
					noProductsText={texts.noProducts}
				/>
			</div>
		</div>
	);
}
